// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container, Row, Col } from 'reactstrap';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowHeader from 'components/molecules/AppointmentFlowHeader/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import Panel from 'components/atoms/Panel';
import FieldSwitch from 'components/atoms/FieldSwitch';
import StreetAddress from 'components/molecules/StreetAddress';
import Alert from 'components/atoms/Alert';
import ClutchErrorModal from 'components/pages/components/ClutchValet/ClutchError';
import RadioButton from 'components/atoms/RadioButton';

// CTA Components
import CTAFlowHeader from 'components/molecules/CTAFlowHeader';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Constants
import { TRANSPORTATION_ROUTE, APPOINTMENT_ROUTE } from 'constants/routes';
import { TRANSPORTATION } from 'constants/transportation';
import { NAVIGATION } from 'constants/navigations';
import { DROPOFF_PREFERENCE_OPTIONS } from 'constants/clutchValet';
import { labelsStepIndicator } from 'constants/CTAConfig';

import './PickUpPage.scss';

// Types
import { gtmUserEventType } from 'types/gtm';
import type { CTA } from 'api/xws/optimizely/optimizely';
import getFirstDateOfWeek from 'date-fns/start_of_week';
import addDays from 'date-fns/add_days';

const ns = 'pickup-page';
const { VALET, VALETLOANER } = TRANSPORTATION;
const { FORWARD } = NAVIGATION;
const {
  SAME_AS_PICKUP_VALET,
  CLUTCH_PARTIAL_VALET,
  ANOTHER_LOCATION_VALET
} = DROPOFF_PREFERENCE_OPTIONS;

type transportationInfo = {|
  configuration: Object,
  data: string,
  label: string
|};

export type Props = {
  goTo: (path: string, direction: string) => void,
  allowsLoaner: boolean,
  setTransportationOption: (transportation: string) => Object,
  transportation: string,
  inputStreetAddress: string,
  setValetComments: (valetComments: string) => void,
  setTransportationInfo: (transportation: transportationInfo) => void,
  valetComments: string,
  pickupAddress: string,
  dropoffAddress: string,
  pickupDropoffSame: boolean,
  dropoffAtDealership: boolean,
  isDropoffSpecificAddress: boolean,
  setDropoffPreference: (dropoffPreference: string) => void,
  setDropoffAddress: (dropoffAddress: string) => void,
  transportations: Array<transportationInfo>,
  intl: IntlShape,
  startTime: string,
  externalValet: string,
  ctaBanner: CTA | null,
  gtmUserEvent: gtmUserEventType,
  showProgressBar: boolean,
  hideBottomCTA: boolean,
  getFirstAvailability: (config: Object) => void,
  getAvailableTimesByDates: (date: Date, endDate: Date, config: Object) => void,
  lastTimeslotSelected: Date,
  masked: boolean,
  errorPickupAddress: boolean,
  errorDropoffAddress: boolean,
  errorClutchFailure: boolean,
  errorInvalidPickUpAddress: boolean,
  errorInvalidDropOffAddress: boolean
};

class PickUpPage extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    (this: any).clutchErrorModal = React.createRef();
  }

  componentDidMount() {
    const {
      pickupDropoffSame,
      dropoffAtDealership,
      isDropoffSpecificAddress,
      setDropoffPreference
    } = this.props;
    if (
      !pickupDropoffSame &&
      !dropoffAtDealership &&
      !isDropoffSpecificAddress
    ) {
      setDropoffPreference(SAME_AS_PICKUP_VALET);
    }
  }

  componentDidUpdate() {
    const { errorClutchFailure } = this.props;
    (this: any).clutchErrorModal.current.setShowModal(errorClutchFailure);
  }
  clutchErrorModal: React.Ref<any>;
  handleLoanerCar = () => {
    const { setTransportationOption, transportation } = this.props;
    if (!transportation || transportation !== VALETLOANER) {
      setTransportationOption(VALETLOANER);
    } else {
      setTransportationOption(VALET);
    }
  };

  handleDropOffAddress = () => {
    const { setDropoffPreference, setDropoffAddress } = this.props;
    setDropoffPreference(ANOTHER_LOCATION_VALET);
    setDropoffAddress('');
  };

  handleValetComments = (text: string) => {
    const { setValetComments } = this.props;
    setValetComments(text);
  };

  showDropOffOptions() {
    const {
      intl,
      externalValet,
      pickupDropoffSame,
      dropoffAtDealership,
      isDropoffSpecificAddress,
      setDropoffPreference,
      setDropoffAddress,
      pickupAddress
    } = this.props;
    let dropOffOptions;

    if (externalValet) {
      dropOffOptions = (
        <div className={`${ns}__drop-off-radio-group`}>
          <RadioButton
            name="drop-off preference"
            isSelected={pickupDropoffSame}
            onClick={() => {
              setDropoffPreference(SAME_AS_PICKUP_VALET);
              setDropoffAddress(pickupAddress);
            }}
            label={intl.formatMessage({
              id: 'c8.pick.up.same.as.pick.up.address',
              defaultMessage: 'Same as pick up address'
            })}
          />
          <RadioButton
            name="drop-off preference"
            isSelected={dropoffAtDealership}
            onClick={() => setDropoffPreference(CLUTCH_PARTIAL_VALET)}
            label={intl.formatMessage({
              id: 'c8.pick.up.get.vehicle.at.the.dealership',
              defaultMessage: 'I will get my vehicle at the dealership'
            })}
          />
          <RadioButton
            name="drop-off preference"
            isSelected={isDropoffSpecificAddress}
            onClick={this.handleDropOffAddress}
            label={intl.formatMessage({
              id: 'c8.pick.up.deliver.vehicle.to.other.location',
              defaultMessage: 'Deliver my vehicle to another location'
            })}
          />
        </div>
      );
    } else {
      if (pickupDropoffSame) {
        dropOffOptions = (
          <FieldSwitch
            onChange={this.handleDropOffAddress}
            name={'location'}
            className={'mt-4'}
            label={intl.formatMessage({
              id: 'c8.pick.up.same.drop.off.location'
            })}
            checked={pickupDropoffSame}
          />
        );
      }
    }
    return dropOffOptions;
  }

  showFieldsPickUp() {
    const {
      transportation,
      valetComments,
      dropoffAddress,
      pickupAddress,
      isDropoffSpecificAddress,
      intl,
      startTime,
      externalValet,
      errorPickupAddress,
      errorDropoffAddress,
      errorInvalidPickUpAddress,
      errorInvalidDropOffAddress
    } = this.props;
    const hasValetDisclaimer = false;
    // We only expect one 'out of range' failure, either pick up or drop off, not both at the same time
    let errorPickupMessage;
    let errorPickUpAriaLabel;
    let errorDropOffMessage;
    let errorDropOffAriaLabel;
    let pickUpAddressLabel = '';
    let externalValetCls = '';

    if (errorPickupAddress) {
      errorPickupMessage = intl.formatMessage({
        id: 'c8.error.valet.40040',
        defaultMessage: 'Service is not available for this location'
      });
      errorPickUpAriaLabel = intl.formatMessage({
        id: 'c8.aria.error.valet.40040',
        defaultMessage: 'Service is not available for this pick up location'
      });
    }
    if (errorInvalidPickUpAddress) {
      errorPickupMessage = intl.formatMessage({
        id: 'c8.error.valet.40060',
        defaultMessage: 'Invalid address. Enter a valid address.'
      });
      errorPickUpAriaLabel = intl.formatMessage({
        id: 'c8.aria.error.valet.40060',
        defaultMessage: 'Invalid Pick up address. Enter a valid address.'
      });
    }
    if (errorDropoffAddress) {
      errorDropOffMessage = intl.formatMessage({
        id: 'c8.error.valet.40050',
        defaultMessage: 'Service is not available for this location'
      });
      errorDropOffAriaLabel = intl.formatMessage({
        id: 'c8.aria.error.valet.40050',
        defaultMessage: 'Service is not available for this drop off location'
      });
    }
    if (errorInvalidDropOffAddress) {
      errorDropOffMessage = intl.formatMessage({
        id: 'c8.error.valet.40070',
        defaultMessage: 'Invalid address. Enter a valid address.'
      });
      errorDropOffAriaLabel = intl.formatMessage({
        id: 'c8.aria.error.valet.40070',
        defaultMessage: 'Invalid Drop off address. Enter a valid address.'
      });
    }
    if (externalValet) {
      if (hasValetDisclaimer) {
        pickUpAddressLabel = intl.formatMessage({
          id: 'c8.pick.up.dealer.disclaimer',
          defaultMessage:
            'What location would you like us to Pick-up your vehicle? The cost of the service is $30 for vehicle pickup and delivery within a 10 mile radius. Each additional 5-mile interval costs $5 per travel route. Must book 24 hours in advance'
        });
      }
      externalValetCls = 'external-valet';
    } else {
      pickUpAddressLabel = intl.formatMessage({
        id:
          'c8.pick.up.we.will.pick.up.your.vehicle.and.return.it.when.it.is.ready'
      });
    }

    return (
      <Row>
        <Col sm={12} md={6}>
          <Panel className={'py-4 px-3'}>
            <span className={`${ns}__label-address ${externalValetCls}`}>
              {pickUpAddressLabel}
            </span>
            {externalValet && (
              <span
                className={
                  `${ns}__street-input-title` +
                  (hasValetDisclaimer
                    ? ''
                    : ` ${ns}__street-input-title--no-margin`)
                }
              >
                {intl.formatMessage({
                  id: 'c8.pick.up.vehicle.pickup.location',
                  defaultMessage: 'Vehicle pick up location'
                })}
              </span>
            )}
            <StreetAddress
              type={'pick-up'}
              placeholder={intl.formatMessage({
                id: 'c8.pick.up.enter.pick.up.address'
              })}
              initialValue={pickupAddress}
              autoFocus={!startTime || !externalValet}
              errorMessage={errorPickupMessage}
              errorAriaLabel={errorPickUpAriaLabel}
              inputAriaLabel={intl.formatMessage({
                id: 'c8.aria.enter.pick.up.address',
                defaultMessage: 'Enter pick up address'
              })}
            />
            {externalValet && (
              <span
                className={`${ns}__street-input-title ${ns}__street-input-title-drop-off`}
              >
                {intl.formatMessage({
                  id: 'c8.pick.up.vehicle.dropoff.location',
                  defaultMessage: 'Vehicle drop off location'
                })}
              </span>
            )}
            {this.showDropOffOptions()}
            {isDropoffSpecificAddress && (
              <StreetAddress
                type={'drop-off'}
                placeholder={intl.formatMessage({
                  id: 'c8.pick.up.drop.off.address'
                })}
                initialValue={dropoffAddress}
                autoFocus={false}
                errorMessage={errorDropOffMessage}
                errorAriaLabel={errorDropOffAriaLabel}
                inputAriaLabel={intl.formatMessage({
                  id: 'c8.aria.enter.drop.off.address',
                  defaultMessage: 'Enter drop off address'
                })}
              />
            )}
          </Panel>
          {this.props.allowsLoaner && (
            <FieldSwitch
              className={`${ns}__switch-lonner-car`}
              name={'loaner'}
              label={intl.formatMessage({ id: 'c8.pick.up.need.a.loaner.car' })}
              onChange={this.handleLoanerCar}
              checked={transportation === VALETLOANER}
            />
          )}
        </Col>
        <Col sm={12} md={6}>
          <span className={`${ns}__label-comments`}>
            {intl.formatMessage({ id: 'c8.pick.up.comments' })}
          </span>
          <textarea
            className={`${ns}__comment-box`}
            maxLength={256}
            defaultValue={valetComments}
            placeholder={intl.formatMessage({
              id: 'c8.pick.up.pick.up.instruction.call.me.when.you.are.outside'
            })}
            onChange={ev => this.handleValetComments(ev.target.value)}
            aria-label={intl.formatMessage({ id: 'c8.pick.up.comments' })}
          />
        </Col>
      </Row>
    );
  }

  onPickUpPageRoute = async () => {
    const {
      setTransportationOption,
      transportation,
      transportations,
      setTransportationInfo,
      externalValet,
      goTo
    } = this.props;
    const selectedTransportation = transportations.find(
      ({ data }) => data === VALET
    );
    (this: any).clutchErrorModal.current.setShowModal(false);
    if (transportation !== VALETLOANER && transportation !== VALET) {
      setTransportationOption(VALET);
    }
    if (selectedTransportation) {
      setTransportationInfo(selectedTransportation);
    }
    if (externalValet) {
      await this.verifyAddressInRange();
      const {
        errorClutchFailure,
        errorPickupAddress,
        errorDropoffAddress,
        errorInvalidDropOffAddress,
        errorInvalidPickUpAddress
      } = this.props;
      if (
        !errorClutchFailure &&
        !errorPickupAddress &&
        !errorDropoffAddress &&
        !errorInvalidPickUpAddress &&
        !errorInvalidDropOffAddress
      ) {
        goTo(APPOINTMENT_ROUTE, FORWARD);
      }
    } else {
      goTo(APPOINTMENT_ROUTE, FORWARD);
    }
  };

  verifyAddressInRange = async function() {
    const {
      startTime,
      lastTimeslotSelected,
      getFirstAvailability,
      getAvailableTimesByDates
    } = this.props;
    const config = {
      masking: true
    };

    const hasStartTime =
      (startTime && startTime.startTimeFromState) || lastTimeslotSelected;
    if (hasStartTime) {
      const date = getFirstDateOfWeek(startTime);
      const endDate = addDays(date, 13);
      await getAvailableTimesByDates(date, endDate, config);
    } else {
      await getFirstAvailability(config);
    }
  };

  render() {
    const {
      pickupAddress,
      dropoffAddress,
      intl,
      startTime,
      externalValet,
      ctaBanner,
      gtmUserEvent,
      showProgressBar,
      hideBottomCTA,
      dropoffAtDealership
    } = this.props;
    const buttonDisabled =
      !pickupAddress || (!dropoffAtDealership && !dropoffAddress);
    const label = intl.formatMessage({ id: 'c8.header.transportation' });
    const backButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.return.to.previous.page',
      defaultMessage: 'Return to previous page'
    });
    const nextButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.continue.to.select.appointment.date.and.time',
      defaultMessage: 'Continue to select appointment date and time'
    });
    return (
      <AppointmentFlowWizard
        hideFooter={Boolean(hideBottomCTA)}
        header={() => {
          return ctaBanner !== null ? (
            <CTAFlowHeader
              label={label}
              hideServices={false}
              routeTo={TRANSPORTATION_ROUTE}
              backButtonAriaLabel={backButtonAriaLabel}
              nextButtonAriaLabel={nextButtonAriaLabel}
              ctaButtonDisabled={buttonDisabled}
              ctaBanner={ctaBanner}
              onClickCtaButton={() => {
                this.onPickUpPageRoute();
                gtmUserEvent(
                  'NEXT button',
                  'Clicked',
                  `Go to next page through CTA`,
                  `GTM_CURRENT_PAGE > ${APPOINTMENT_ROUTE} page`
                );
              }}
              typeButton={'cta-next'}
            />
          ) : (
            <AppointmentFlowHeader
              label={label}
              routeTo={TRANSPORTATION_ROUTE}
              backButtonAriaLabel={backButtonAriaLabel}
            />
          );
        }}
        footer={() => {
          return !hideBottomCTA ? (
            <AppointmentFlowFooter
              className={''}
              buttonDisabled={buttonDisabled}
              buttonText={intl.formatMessage({ id: 'c8.footer.next' })}
              onClick={() => {
                this.onPickUpPageRoute();
              }}
              ariaLabel={nextButtonAriaLabel}
            />
          ) : null;
        }}
        body={() => (
          <React.Fragment>
            <ClutchErrorModal
              key="clutchError"
              modalRef={this.clutchErrorModal}
              tryAgainCallback={this.onPickUpPageRoute}
            />
            {startTime &&
              externalValet && (
                <Alert color="primary" animation="top-down">
                  <div
                    className={`${ns}__change_alert`}
                    tabIndex={0}
                    ref={(this: any).message}
                    aria-label={intl.formatMessage({
                      id: 'c8.aria.pick.up.modify.alert',
                      defaultMessage:
                        'Changing the pick up or drop off location may affect the appointment time'
                    })}
                  >
                    {intl.formatMessage({
                      id: 'c8.pick.up.modify.alert',
                      defaultMessage:
                        'Changing the pick up or drop off location may affect the appointment time'
                    })}
                  </div>
                </Alert>
              )}
            {showProgressBar && (
              <CtaStepIndicator
                htmlId="VehiclePageStepIndicator"
                labels={labelsStepIndicator(intl)}
                activeStep={3}
              />
            )}
            <Container className={ns}>
              <Row>
                <Col className={'d-flex flex-column'}>
                  <h1 className={`${ns}__title`}>
                    {externalValet
                      ? intl.formatMessage({
                          id: 'c8.pick.up.valet.service',
                          defaultMessage: 'Valet service'
                        })
                      : intl.formatMessage({
                          id: 'c8.pick.up.pickup.information'
                        })}
                  </h1>
                </Col>
              </Row>
              {this.showFieldsPickUp()}
              <Row className={`${ns}__note d-block align-items-center`}>
                <span>
                  {intl.formatMessage({
                    id:
                      'c8.pick.up.dealership.is.going.to.call.you.regarding:next.steps'
                  })}
                </span>
              </Row>
            </Container>
          </React.Fragment>
        )}
      />
    );
  }
}

export default PickUpPage;



// WEBPACK FOOTER //
// ./src/components/pages/PickUpPage/PickUpPage.js